import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function QualityDashboard() {
  const { data: qualityStats } = useQuery({
    queryKey: ['quality-dashboard-stats'],
    queryFn: async () => {
      const [qualityRes, validationRes] = await Promise.all([
        supabase.from('content_quality_analyses').select('*').order('analyzed_at', { ascending: false }).limit(100),
        supabase.from('medical_validations').select('*').order('validated_at', { ascending: false }).limit(100),
      ]);

      const qualities = qualityRes.data || [];
      const validations = validationRes.data || [];

      const avgQuality = qualities.length > 0 
        ? Math.round(qualities.reduce((sum, q) => sum + q.overall_score, 0) / qualities.length)
        : 0;

      const qualityDistribution = {
        excellent: qualities.filter(q => q.overall_score >= 85).length,
        good: qualities.filter(q => q.overall_score >= 70 && q.overall_score < 85).length,
        needsImprovement: qualities.filter(q => q.overall_score < 70).length,
      };

      const validationStatus = {
        accurate: validations.filter(v => v.overall_status === 'accurate').length,
        questionable: validations.filter(v => v.overall_status === 'questionable').length,
        inaccurate: validations.filter(v => v.overall_status === 'inaccurate').length,
      };

      const avgConfidence = validations.length > 0
        ? Math.round(validations.reduce((sum, v) => sum + v.confidence_score, 0) / validations.length)
        : 0;

      return {
        avgQuality,
        qualityDistribution,
        validationStatus,
        avgConfidence,
        totalAnalyses: qualities.length,
        totalValidations: validations.length,
      };
    },
  });

  const { data: recentIssues } = useQuery({
    queryKey: ['quality-recent-issues'],
    queryFn: async () => {
      const { data } = await supabase
        .from('content_quality_analyses')
        .select('*')
        .lt('overall_score', 70)
        .order('analyzed_at', { ascending: false })
        .limit(5);
      return data || [];
    },
  });

  const { data: improvementTasks } = useQuery({
    queryKey: ['quality-improvement-tasks'],
    queryFn: async () => {
      const { data } = await supabase
        .from('quality_improvement_tasks')
        .select('*')
        .in('status', ['open', 'in_progress'])
        .order('priority', { ascending: false })
        .limit(5);
      return data || [];
    },
  });

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Quality</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{qualityStats?.avgQuality || 0}%</div>
            <Progress value={qualityStats?.avgQuality || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Medical Accuracy</CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{qualityStats?.avgConfidence || 0}%</div>
            <p className="text-xs text-muted-foreground mt-2">
              {qualityStats?.validationStatus.accurate || 0} accurate validations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Needs Review</CardTitle>
            <AlertTriangle className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{qualityStats?.qualityDistribution.needsImprovement || 0}</div>
            <p className="text-xs text-muted-foreground mt-2">
              Content with quality &lt; 70%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tasks</CardTitle>
            <Clock className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{improvementTasks?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-2">
              Improvement tasks pending
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Insights */}
      <Tabs defaultValue="issues" className="w-full">
        <TabsList>
          <TabsTrigger value="issues">Recent Issues</TabsTrigger>
          <TabsTrigger value="tasks">Improvement Tasks</TabsTrigger>
          <TabsTrigger value="distribution">Quality Distribution</TabsTrigger>
        </TabsList>

        <TabsContent value="issues" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Needing Attention</CardTitle>
              <CardDescription>Items with quality score below 70%</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentIssues?.map((issue) => (
                  <div key={issue.id} className="flex items-start justify-between border-b pb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{issue.content_type}</Badge>
                        <span className="text-sm font-medium">Score: {issue.overall_score}%</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {issue.content_text.substring(0, 100)}...
                      </p>
                      <div className="flex gap-2 mt-2">
                        {Array.isArray(issue.issues) && issue.issues.slice(0, 2).map((iss: string, i: number) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {iss}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                {!recentIssues?.length && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No quality issues found. Great work!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Improvement Tasks</CardTitle>
              <CardDescription>Tasks generated from quality analyses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {improvementTasks?.map((task) => (
                  <div key={task.id} className="flex items-start justify-between border-b pb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={task.priority === 'critical' ? 'destructive' : 'secondary'}>
                          {task.priority}
                        </Badge>
                        <Badge variant="outline">{task.status}</Badge>
                      </div>
                      <p className="text-sm mt-1">{task.task_description}</p>
                    </div>
                  </div>
                ))}
                {!improvementTasks?.length && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No active improvement tasks
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribution" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quality Distribution</CardTitle>
              <CardDescription>Content quality breakdown</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Excellent (85%+)</span>
                  <span className="text-sm text-muted-foreground">
                    {qualityStats?.qualityDistribution.excellent || 0} items
                  </span>
                </div>
                <Progress 
                  value={(qualityStats?.qualityDistribution.excellent || 0) / (qualityStats?.totalAnalyses || 1) * 100} 
                  className="h-2 bg-success/20"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Good (70-84%)</span>
                  <span className="text-sm text-muted-foreground">
                    {qualityStats?.qualityDistribution.good || 0} items
                  </span>
                </div>
                <Progress 
                  value={(qualityStats?.qualityDistribution.good || 0) / (qualityStats?.totalAnalyses || 1) * 100} 
                  className="h-2 bg-primary/20"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Needs Improvement (&lt;70%)</span>
                  <span className="text-sm text-muted-foreground">
                    {qualityStats?.qualityDistribution.needsImprovement || 0} items
                  </span>
                </div>
                <Progress 
                  value={(qualityStats?.qualityDistribution.needsImprovement || 0) / (qualityStats?.totalAnalyses || 1) * 100} 
                  className="h-2 bg-warning/20"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
